import { AfterViewInit, Component, computed, Signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { DocumentRequestPayload, DocumentRequirements, RemainingDocumentRequirement } from '@app/app/interfaces/document-requirements.model';
import { DocumentsState } from '@app/app/store/documents/documents.state';
import { Store } from '@ngxs/store';
import { DocumentRequirementDialogComponent } from '../documents/document-requirement-dialog/document-requirement-dialog.component';
import { CommonModule } from '@angular/common';
import { SaasFeaturesState } from '@app/app/store/saas-features/saas-features.state';
import { ApplicationDetailsState } from '@app/app/store/application-details/application-details.state';
import { ApplicationDetails } from '@app/app/interfaces/application-details.model';
import { formatDocumentRequestsPayload } from './document-requirements-helpers';
import { DocumentsActions as DA } from '@app/app/store/documents/documents.actions';
import { DeleteDocumentRequirementDialogComponent } from '../documents/document-requirement-dialog/document-requirement-delete-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { ProcessTimestampsTooltipComponent } from '../process-timestamps-tooltip/process-timestamps-tooltip.component';
import { CdkScrollable, Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'app-document-requirements',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatExpansionModule,
    MatIconModule,
    MatIconButton,
    MatTooltipModule,
    ProcessTimestampsTooltipComponent,
    OverlayModule,
  ],
  templateUrl: './document-requirements.component.html',
  styleUrl: './document-requirements.component.scss'
})
export class DocumentRequirementsComponent implements AfterViewInit {
  documentRequirements: Signal<DocumentRequirements>;
  uploadedButNotAccepted: Signal<RemainingDocumentRequirement[]>;
  notUploaded: Signal<RemainingDocumentRequirement[]>;
  canAddDocumentRequirements: Signal<boolean>;
  applicationDetails: Signal<ApplicationDetails>;
  acceptedFraction: Signal<string>;
  hasRequirements: Signal<boolean>;
  hasEsignaturePlus: Signal<boolean>;
  showDocusignTimestamps: string | null = null;
  openDocumentRequirementListItemRef: HTMLElement | null = null;
  openESignaturePopoverRef : HTMLElement | null = null;
  scrollStrategy: ScrollStrategy | null = null;
  
  constructor(
    private dialog: MatDialog,
    private _store: Store,
    private overlay: Overlay,
  ) {
    this.documentRequirements = this._store.selectSignal(DocumentsState.documentRequirements);
    this.canAddDocumentRequirements = this._store.selectSignal(SaasFeaturesState.saasPermitted('canAddDocumentRequirements'));
    this.applicationDetails = this._store.selectSignal(ApplicationDetailsState.applicationDetails);
    this.hasEsignaturePlus = this._store.selectSignal<boolean>(SaasFeaturesState.saasPermitted('eaSignaturePlus'));

    this.hasRequirements = computed(() => {
      return (
        this.documentRequirements().satisfied.length > 0 ||
        this.documentRequirements().remaining.length > 0
      );
    });

    this.acceptedFraction = computed(() => {
      if (!this.hasRequirements()) {
        return 'No requirements available';
      }

      return `(${this.documentRequirements().satisfied.length}/${this.documentRequirements().satisfied.length +
        this.documentRequirements().remaining.length
        } Accepted)`;
    });

    this.notUploaded = computed(() => {
      return this.documentRequirements().remaining.filter((dr) => !dr.isSatisfied);
    });

    this.uploadedButNotAccepted = computed(() => {
      return this.documentRequirements().remaining.filter((dr) => dr.isSatisfied);
    });
  }

  ngAfterViewInit(): void {
    this.scrollStrategy = this.overlay.scrollStrategies.close();
  }

  openDocumentRequirementDialog(event): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DocumentRequirementDialogComponent, {
      position: { top: '10%' },
      width: '408px',
      disableClose: true,
    });

    dialogRef.componentInstance.onSave.subscribe({
      next: (payload: DocumentRequestPayload) => {
        const dealId = this.applicationDetails().deal.id;
        const documentRequestsPayload = formatDocumentRequestsPayload(payload)
        this._store.dispatch(new DA.PostDocumentRequirements(dealId, documentRequestsPayload));
      }
    });
  }

  handleRequirementDelete(event, requiredDocument): void {
    event.stopPropagation();
    this.dialog.open(DeleteDocumentRequirementDialogComponent, {
      width: '375px',
      data: { requiredDocument, dealId: this.applicationDetails().deal.id }
    });
  }

  handleEsignatureClick(event, requiredDocument): void {
    event.stopPropagation();
    const origin = event.target as HTMLElement
    if (this.showDocusignTimestamps) {
      this.showDocusignTimestamps = null;
      this.openESignaturePopoverRef  = null;
      return
    }
    this.openESignaturePopoverRef = origin
    const parent = this.openESignaturePopoverRef.closest('.document-requirement') as HTMLElement
    this.openDocumentRequirementListItemRef = parent;
    this.showDocusignTimestamps = requiredDocument?.documentCategory?.name
  }

  handleDocRequirementMouseEnter(event, document) {
    if (document?.source === 'documentRequest' || (this.hasEsignaturePlus() && document.docusignEnvelope)) {
      const item = event.target as HTMLElement
      item.classList.add("hover-effect")
    }
  }

  handleDocRequirementMouseLeave(event, document) {
    if (!!this.showDocusignTimestamps && document?.documentCategory?.name === this.showDocusignTimestamps) {
      return
    }
    const item = event.target as HTMLElement
    return item.classList.remove("hover-effect")
  }

  handleOverlayOutsideClick(): void {
      this.openDocumentRequirementListItemRef?.classList.remove('hover-effect');
      this.showDocusignTimestamps = null;
      this.openDocumentRequirementListItemRef = null;
      this.openESignaturePopoverRef = null;
  }

  handleOverlayClose(): void {
    this.showDocusignTimestamps = null;
    this.openDocumentRequirementListItemRef = null;
    this.openESignaturePopoverRef = null;
  }
}


